import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import { MONEY_FORMAT_OPTIONS } from '@sophocles/data-grids';
import { productSlice } from '@sophocles/features/warehouse/products/slice';
import { formatNumber } from '@sophocles/utils/formatUtils';

const OrderSaleSummaryTable = ({ items, shippingCost = null, showShipped }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  let cumulativeTotal = 0; // Local running total
  let cumulativeQtyOrdered = 0; // Local running total
  let cumulativeQtyShipped = 0; // Local running total

  const calculateSubtotal = item => {
    return (showShipped ? item.qty_fill : item.qty_order) * item.item_price;
  };

  const calculatePrepaidSubtotal = item => {
    return item.prepaid_cost * (showShipped ? item.qty_fill : item.qty_order);
  };

  const productIds = items?.map(item => item.item);

  const products = useSelector(state => productSlice.selectors.selectAllEntities(state)).filter(product =>
    productIds?.includes(product.id),
  );

  return (
    <TableContainer>
      <Table aria-label={t('warehouse.summaryTableAria', 'summary table')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('general.item', 'Item')}</TableCell>
            <TableCell align="right">{t('general.quantity', 'Quantity')}</TableCell>
            {showShipped && <TableCell align="right">{t('warehouse.shipped', 'Shipped')}</TableCell>}
            <TableCell align="right">{t('general.price', 'Price')}</TableCell>
            <TableCell align="right">{t('warehouse.subtotal', 'Subtotal')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => {
            const product = products.find(product => product.id === item.item);
            const subtotal = calculateSubtotal(item);
            cumulativeTotal += subtotal; // Update cumulative total
            cumulativeQtyOrdered += item.qty_order;
            cumulativeQtyShipped += item.qty_fill;

            const itemPrice = parseFloat(item.item_price);
            let prepaidPrice = 0;

            const mainRow = (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant="body1">
                    {product != null ? `${product.item_part_num}: ${product.item_name}` : <Skeleton />}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {product != null ? product.item_description : <Skeleton />}
                  </Typography>
                </TableCell>
                <TableCell align="right">{item.qty_order}</TableCell>
                {showShipped && <TableCell align="right">{item.qty_fill}</TableCell>}
                <TableCell align="right">${formatNumber(itemPrice, user, MONEY_FORMAT_OPTIONS)}</TableCell>
                <TableCell align="right">${formatNumber(subtotal, user, MONEY_FORMAT_OPTIONS)}</TableCell>
              </TableRow>
            );

            if (item.has_radios && item.radio_billing_type === 'Prepaid') {
              prepaidPrice = parseFloat(item.prepaid_cost);
              cumulativeTotal += calculatePrepaidSubtotal(item); // Update cumulative total
            }

            return (
              <React.Fragment key={index}>
                {mainRow}
                {item.has_radios && product != null && !product.is_service && item.radio_billing_type === 'Prepaid' && (
                  <TableRow>
                    <TableCell style={{ paddingLeft: '30px' }}>
                      <Typography variant="body2" color="textSecondary">
                        {t('warehouse.prepaidTerm', 'Prepaid Term - {{months}} Months', {
                          months: item.term_length_prepaid,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{item.qty_order}</TableCell>
                    {showShipped && <TableCell align="right"></TableCell>}
                    <TableCell align="right">${formatNumber(prepaidPrice, user, MONEY_FORMAT_OPTIONS)}</TableCell>
                    <TableCell align="right">
                      ${formatNumber(calculatePrepaidSubtotal(item), user, MONEY_FORMAT_OPTIONS)}
                    </TableCell>
                  </TableRow>
                )}
                {item.has_radios && showShipped && item.iccids?.length > 0 && (
                  <TableRow>
                    <TableCell style={{ paddingLeft: '30px' }} colSpan={5}>
                      <Typography variant="body2" color="textSecondary">
                        {t('warehouse.iccidsLabel', 'ICCIDs')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.iccids.map(i => i.iccid).join(', ')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}

          {shippingCost != null && (
            <TableRow>
              <TableCell>
                <Typography variant="body1">{t('warehouse.shipping', 'Shipping')}</Typography>
              </TableCell>
              <TableCell align="right"></TableCell>
              {showShipped && <TableCell align="right"></TableCell>}
              <TableCell align="right"></TableCell>
              <TableCell align="right">${formatNumber(shippingCost, user, MONEY_FORMAT_OPTIONS)}</TableCell>
            </TableRow>
          )}

          {/* Highlighted Total Row */}
          <TableRow>
            <TableCell align="right">
              <Typography variant="h7" fontWeight="bold">
                {t('warehouse.totals', 'Totals')}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="h7" fontWeight="bold">
                {cumulativeQtyOrdered}
              </Typography>
            </TableCell>
            {showShipped && (
              <TableCell align="right">
                <Typography variant="h7" fontWeight="bold">
                  {cumulativeQtyShipped}
                </Typography>
              </TableCell>
            )}
            <TableCell align="right"></TableCell>
            <TableCell align="right">
              <Typography variant="h7" fontWeight="bold">
                ${formatNumber(cumulativeTotal + (shippingCost ?? 0), user, MONEY_FORMAT_OPTIONS)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrderSaleSummaryTable;
